import React, { useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    Form,
    Row,
    Col,
    Container,
    Alert,
} from 'react-bootstrap';
import LoadingSpinner from '../components/LoadingSpinner';
import { calculateDirection } from '../utils';
import Footer from '../components/Footer';
import { QuestionSquareFill } from 'react-bootstrap-icons';
import SlopeHelpModal from '../components/modals/SlopeHelpModal';
import RoofHelpModal from '../components/modals/RoofHelpModal';
import ShadeModal from '../components/modals/ShadeModal';
import ResultsModal from '../components/modals/ResultsModal';
import EnergyModal from '../components/modals/EnergyModal';
import AssumptionsModal from '../components/modals/AssumptionsModal';
import AddonsSection from '../components/AddonsSection';
import * as Sentry from '@sentry/react';
import { useInitialData } from '../hooks/useInitialData';
import { useSolarCalculation, getCalculationErrorMessage } from '../hooks/useSolarCalculation';
import { getSolarData, isPricingUnavailable } from '../apiCalls';
import { getInitialFormValues } from '../utils/formDefaults';
import { hasCalculationResult } from '../utils/calculationResult';
import { initJourneyExitTracking, noteJourneyField, startJourneyForm, trackJourney } from '../utils/journey';
import {
    BodyContainer,
    BodyRow,
    FormSecondHeading,
    FormThirdHeading,
    SubmitButton,
    CalculatorForm,
    CalculatorStepper,
    CalculatorStep,
    CalculatorStepNumber,
    CalculatorActions,
} from '../styles/LayoutStyles';
import { IconRow } from '../styles/ComponentStyles';

const BATTERY_ADDON_KEYS = ['firstBatteryCost', 'secondBatteryCost'];
const FORM_STORAGE_KEY = 'solarCalcForm';
const FORM_STORAGE_TTL_MS = 1000 * 60 * 60 * 24 * 21; // 21 days

const getPanelComparisonCounts = (currentPanelCount, panelOptions) => {
    const counts = panelOptions
        .map(({ value }) => Number(value))
        .filter(Number.isFinite)
        .sort((first, second) => first - second);
    const currentIndex = counts.indexOf(Number(currentPanelCount));

    if (counts.length <= 3) return counts;
    if (currentIndex < 0) {
        return [counts[0], counts[Math.floor(counts.length / 2)], counts[counts.length - 1]];
    }

    const lastIndex = counts.length - 1;
    const preferredIndexes = currentIndex <= 1
        ? [currentIndex, currentIndex + 2, currentIndex + 4]
        : currentIndex >= lastIndex - 1
            ? [currentIndex - 4, currentIndex - 2, currentIndex]
            : [currentIndex - 2, currentIndex, currentIndex + 2];
    const selectedIndexes = new Set(
        preferredIndexes.map(index => Math.max(0, Math.min(lastIndex, index)))
    );

    for (let distance = 1; selectedIndexes.size < 3; distance += 1) {
        if (currentIndex - distance >= 0) selectedIndexes.add(currentIndex - distance);
        if (selectedIndexes.size < 3 && currentIndex + distance <= lastIndex) {
            selectedIndexes.add(currentIndex + distance);
        }
    }

    return [...selectedIndexes]
        .sort((first, second) => first - second)
        .map(index => counts[index]);
};

const initialState = {
    fitting: 'roof',
    type: 'tile',
    size: '',
    compassBearing: 180,
    direction: 'South',
    roofSlope: 30,
    shadeFactor: 0.96,
    inverterOption: 'standard',
    energyHabit: 'inHalfDay',
    annualConsumptionKwh: 3500,
    postcode: '',
    battery: 'none',
};

const POSTCODE_FORMAT_MESSAGE = 'Invalid postcode format, please use the format AA1 1AA or M1 1AA';

const isValidPostcodeFormat = (value) => {
    if (!value) return false;
    const normalized = value.trim().replace(/\s+/g, '');
    return /^[A-Za-z]{1,2}\d[A-Za-z\d]?\d[A-Za-z]{2}$/i.test(normalized)
        || /^\d{4,5}$/i.test(normalized)
        || /^[A-Za-z]\d{2}\s?\d[A-Za-z]{2}$/i.test(normalized);
};

const BatteryInverterSync = ({ values, setFieldValue }) => {
    const hadBatteryRef = useRef(Boolean(values.battery && values.battery !== 'none'));
    useEffect(() => {
        const hasBattery = Boolean(values.battery && values.battery !== 'none');
        if (hasBattery && values.inverterOption !== 'hybrid') {
            setFieldValue('inverterOption', 'hybrid');
        } else if (!hasBattery && hadBatteryRef.current && values.inverterOption === 'hybrid') {
            setFieldValue('inverterOption', 'standard');
        }
        hadBatteryRef.current = hasBattery;
    }, [values.battery, values.inverterOption, setFieldValue]);
    return null;
};

const getSchema = (addons = []) => {
    const addonShape = { battery: Yup.string() };
    addons.forEach(({ key }) => {
        if (!BATTERY_ADDON_KEYS.includes(key)) {
            addonShape[key] = Yup.boolean();
        }
    });

    return Yup.object().shape({
        fitting: Yup.string().required('Required'),
        type: Yup.string().when('fitting', {
            is: 'roof',
            then: (schema) => schema.required('Roof type is required'),
        }),
        size: Yup.number().required('Required'),
        compassBearing: Yup.number().when('fitting', {
            is: 'roof',
            then: (schema) => schema.required('Roof direction is required'),
        }),
        roofSlope: Yup.number().when('fitting', {
            is: 'roof',
            then: (schema) => schema.required('Roof slope is required'),
        }),
        shadeFactor: Yup.number().when('fitting', {
            is: 'roof',
            then: (schema) => schema.required('Shading factor is required'),
        }),
        inverterOption: Yup.string().required('Required'),
        annualConsumptionKwh: Yup.number().typeError('Enter annual usage in kWh').positive('Enter a value greater than zero').required('Required'),
        postcode: Yup.string()
            .required('Please enter your postcode to continue.')
            .test('valid-postcode', POSTCODE_FORMAT_MESSAGE, (value) => isValidPostcodeFormat(value)),
        ...addonShape,
    });
};

export const normalizeCalculationValues = (values) => {
    const normalized = {
        ...values,
        postcode: (values.postcode || '').trim().replace(/\s+/g, ' ').toUpperCase(),
        scaffoldingRequired: values.scaffoldingRequired ?? false,
    };
    if (typeof normalized.energyHabit === 'string' && !normalized.energyHabit.trim()) {
        delete normalized.energyHabit;
    }

    if (normalized.fitting === 'ground') {
        return {
            ...normalized,
            type: 'ground',
            compassBearing: 180,
            roofSlope: 0,
            shadeFactor: 1,
        };
    }

    return normalized;
};

function Home() {
    const [formState, setFormState] = useState(() => {
        const savedForm = localStorage.getItem(FORM_STORAGE_KEY);
        if (!savedForm) return initialState;
        try {
            const parsed = JSON.parse(savedForm);
            const hasEnvelope = parsed && typeof parsed === 'object' && parsed.values;
            const savedAt = hasEnvelope ? parsed.savedAt : null;
            const savedValues = hasEnvelope ? parsed.values : parsed;

            if (savedAt && (Date.now() - savedAt > FORM_STORAGE_TTL_MS)) {
                localStorage.removeItem(FORM_STORAGE_KEY);
                return initialState;
            }

            return getInitialFormValues(savedValues, []);
        } catch {
            localStorage.removeItem(FORM_STORAGE_KEY);
            return initialState;
        }
    });

    const [rhModalIsOpen, setRHModalIsOpen] = useState(false);
    const [shModalIsOpen, setSHModalIsOpen] = useState(false);
    const [shadeModalIsOpen, setShadeModalIsOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [resultsModalIsOpen, setResultsModalIsOpen] = useState(false);
    const [energyModalIsOpen, setEnergyModalIsOpen] = useState(false);
    const [assumptionsModalIsOpen, setAssumptionsModalIsOpen] = useState(false);
    const [panelComparisons, setPanelComparisons] = useState({});
    const [panelComparisonCounts, setPanelComparisonCounts] = useState([]);
    const [selectedPanelCount, setSelectedPanelCount] = useState(null);
    const [isLoadingPanelComparisons, setIsLoadingPanelComparisons] = useState(false);
    const [panelComparisonError, setPanelComparisonError] = useState('');
    const [comparisonPricingUnavailable, setComparisonPricingUnavailable] = useState(false);
    const comparisonRequestId = useRef(0);

    const { panelOptions, addons, isLoading: isLoadingData, error: dataError } = useInitialData();
    const { results, isCalculating, error: calcError, pricingUnavailable, calculate, reset } = useSolarCalculation();

    useEffect(() => initJourneyExitTracking(), []);

    useEffect(() => {
        trackJourney('calculator_viewed');
    }, []);

    useEffect(() => {
        if (activeStep < 3) trackJourney('step_viewed', { step: activeStep + 1 });
    }, [activeStep]);

    // Merge addon keys into form state when addons load
    useEffect(() => {
        if (addons.length === 0) return;
        setFormState(prev => {
            const updated = { ...prev };
            if (!('battery' in updated)) updated.battery = 'none';
            addons.forEach(({ key }) => {
                if (!BATTERY_ADDON_KEYS.includes(key) && !(key in updated)) {
                    updated[key] = false;
                }
            });
            return updated;
        });
    }, [addons]);

    useEffect(() => {
        if (panelOptions.length > 0) {
            setFormState(prev => getInitialFormValues(prev, panelOptions));
        }
    }, [panelOptions]);

    // Open results modal when a calculation completes
    useEffect(() => {
        if (hasCalculationResult(results)) {
            trackJourney('results_viewed');
            setResultsModalIsOpen(true);
        } else {
            setResultsModalIsOpen(false);
        }
    }, [results]);

    const openHelpModal = (modalName, setter) => {
        setter(true);
    };

    const setDirectionString = (values) => {
        if (!values.direction) return calculateDirection(values.compassBearing);
        return values.direction;
    };

    const buildBearingOptions = () => {
        const arr = [];
        for (let i = 0; i <= 360; i += 5) {
            arr.push(<option key={i} value={i}>{i}</option>);
        }
        return arr;
    };

    const buildSlopeOptions = () => {
        const arr = [];
        for (let i = 0; i <= 90; i += 5) {
            arr.push(<option key={i} value={i}>{i}</option>);
        }
        return arr;
    };

    const loadPanelComparisons = async (submittedValues, primaryResults) => {
        const requestId = ++comparisonRequestId.current;
        const comparisonCounts = getPanelComparisonCounts(submittedValues.size, panelOptions);
        setPanelComparisonCounts(comparisonCounts);
        setPanelComparisons({});
        setIsLoadingPanelComparisons(true);
        setPanelComparisonError('');

        try {
            const comparisons = await Promise.all(comparisonCounts.map(async (panelCount) => {
                if (panelCount === Number(submittedValues.size)) {
                    return [panelCount, primaryResults];
                }

                const response = await getSolarData({ ...submittedValues, size: panelCount });
                if (!hasCalculationResult(response.data)) throw new Error('Invalid comparison response');
                return [panelCount, response.data];
            }));

            if (requestId === comparisonRequestId.current) {
                setPanelComparisons(Object.fromEntries(comparisons));
            }
        } catch (error) {
            if (requestId === comparisonRequestId.current) {
                setPanelComparisons({});
                setPanelComparisonCounts([]);
                setSelectedPanelCount(null);
                setResultsModalIsOpen(false);
                reset();
                setComparisonPricingUnavailable(isPricingUnavailable(error));
                setPanelComparisonError(getCalculationErrorMessage(error));
            }
        } finally {
            if (requestId === comparisonRequestId.current) {
                setIsLoadingPanelComparisons(false);
            }
        }
    };

    const submitCalculation = async (values) => {
        if (isLoadingData || dataError || pricingUnavailable || comparisonPricingUnavailable || isCalculating) return;
        comparisonRequestId.current += 1;
        setPanelComparisons({});
        setPanelComparisonCounts([]);
        setSelectedPanelCount(null);
        setPanelComparisonError('');
        setIsLoadingPanelComparisons(false);
        setResultsModalIsOpen(false);
        reset();
        const hasBattery = Boolean(values.battery && values.battery !== 'none');
        const effectiveInverter = (hasBattery || values.inverterOption === 'hybrid') ? 'hybrid' : (values.inverterOption || 'standard');
        const submittedValues = {
            ...normalizeCalculationValues(values),
            inverterOption: effectiveInverter,
        };
        startJourneyForm();
        setFormState(submittedValues);
        localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify({
            savedAt: Date.now(),
            values: submittedValues,
        }));
        Sentry.addBreadcrumb({
            category: 'form',
            message: 'Solar calculation submitted',
            data: {
                fitting: submittedValues.fitting,
                size: submittedValues.size,
                inverterOption: submittedValues.inverterOption,
            },
            level: 'info',
        });
        const calculationResults = await calculate(submittedValues);
        if (calculationResults) {
            setSelectedPanelCount(Number(submittedValues.size));
            await loadPanelComparisons(submittedValues, calculationResults);
        }
    };

    const handleSubmit = async (values) => {
        await submitCalculation(values);
    };

    const handlePanelOptionSelect = (panelCount) => {
        if (!panelComparisons[panelCount]) return;

        const nextValues = {
            ...(formState || initialState),
            size: panelCount,
            postcode: (formState?.postcode || '').trim().replace(/\s+/g, ' ').toUpperCase(),
        };
        setSelectedPanelCount(panelCount);
        setFormState(nextValues);
        localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify({
            savedAt: Date.now(),
            values: nextValues,
        }));
    };

    const isLoading = isLoadingData || isCalculating;

    return (
        <Container fluid className='main-container p-0'>
            <SlopeHelpModal shModalIsOpen={shModalIsOpen} closeSHModal={() => setSHModalIsOpen(false)} />
            <RoofHelpModal rhModalIsOpen={rhModalIsOpen} closeRHModal={() => setRHModalIsOpen(false)} />
            <ShadeModal shadeModalIsOpen={shadeModalIsOpen} closeShadeModal={() => setShadeModalIsOpen(false)} />
            <ResultsModal
                resultsModalIsOpen={resultsModalIsOpen && hasCalculationResult(results)}
                openAssumptionsModal={() => {
                    setAssumptionsModalIsOpen(true);
                }}
                closeResultsModal={() => setResultsModalIsOpen(false)}
                results={panelComparisons[selectedPanelCount] || results || {}}
                formValues={{
                    ...formState,
                    size: selectedPanelCount ?? formState?.size,
                }}
                onSelectPanelOption={handlePanelOptionSelect}
                panelComparisons={panelComparisons}
                panelComparisonCounts={panelComparisonCounts}
                selectedPanelCount={selectedPanelCount}
                isLoadingPanelComparisons={isLoadingPanelComparisons}
                panelComparisonError={panelComparisonError}
            />
            <EnergyModal energyModalIsOpen={energyModalIsOpen} closeEnergyModal={() => setEnergyModalIsOpen(false)} />
            <AssumptionsModal
                assumptionsModalIsOpen={assumptionsModalIsOpen}
                closeAssumptionsModal={() => setAssumptionsModalIsOpen(false)}
                calculationData={results?.calculationData || {}}
            />

            <BodyContainer as={Container} fluid>
                <Row>
                    <Col>
                        {/*<BodyRow as={Row}>*/}
                        {/*    <Col xs={12}>*/}
                        {/*        <FormHeading>Solar Performance Calculator</FormHeading>*/}
                        {/*    </Col>*/}
                        {/*</BodyRow>*/}

                        {/*<FormHeaderContent as={Col}>*/}
                        {/*    <FormSecondHeading>*/}
                        {/*        Welcome to the Solmatix Performance calculation tool*/}
                        {/*    </FormSecondHeading>*/}
                        {/*    <FormThirdHeading>*/}
                        {/*        Tell us a few details about your property and we will estimate the solar output, savings and payback for a typical UK home in a few quick steps.*/}
                        {/*    </FormThirdHeading>*/}
                        {/*    <FormThirdHeading>*/}
                        {/*        We have pre-filled the most common setup for a roof-mounted system to make this quicker. You can adjust anything that does not match your home.*/}
                        {/*    </FormThirdHeading>*/}
                        {/*</FormHeaderContent>*/}

                        {dataError && (
                            <Alert role='alert' aria-live='assertive' variant='danger' className='mx-3'>{dataError}</Alert>
                        )}
                        {(calcError || panelComparisonError) && (
                            <Alert role='alert' aria-live='assertive' variant='danger' className='mx-3'>
                                {calcError || panelComparisonError}
                            </Alert>
                        )}

                        <Formik
                            onSubmit={handleSubmit}
                            validationSchema={getSchema(addons)}
                            initialValues={formState || initialState}
                            enableReinitialize={false}
                            validateOnBlur={false}
                            validateOnChange={false}
                        >
                            {({ values, touched, errors, handleSubmit: formikSubmit, handleChange, setFieldTouched, setFieldValue, setFieldError }) => {
                                const validateStep = (stepIndex) => {
                                    const stepChecks = {};
                                    if (stepIndex === 0) {
                                        stepChecks.postcode = !values.postcode
                                            ? 'Please enter your postcode to continue.'
                                            : !isValidPostcodeFormat(values.postcode)
                                                ? POSTCODE_FORMAT_MESSAGE
                                                : undefined;
                                        stepChecks.annualConsumptionKwh = Number(values.annualConsumptionKwh) > 0 ? undefined : 'Enter annual usage in kWh';
                                        return stepChecks;
                                    }
                                    if (stepIndex === 1) {
                                        stepChecks.size = values.size ? undefined : 'Required';
                                        stepChecks.fitting = values.fitting ? undefined : 'Required';
                                        if (values.fitting === 'roof') {
                                            stepChecks.type = values.type ? undefined : 'Roof type is required';
                                            stepChecks.compassBearing = (values.compassBearing === '' || values.compassBearing == null) ? 'Roof direction is required' : undefined;
                                            stepChecks.roofSlope = (values.roofSlope === '' || values.roofSlope == null) ? 'Roof slope is required' : undefined;
                                            stepChecks.shadeFactor = (values.shadeFactor === '' || values.shadeFactor == null) ? 'Shading factor is required' : undefined;
                                        }
                                        return stepChecks;
                                    }
                                    if (stepIndex === 2) {
                                        stepChecks.inverterOption = values.inverterOption ? undefined : 'Required';
                                        return stepChecks;
                                    }
                                    return {};
                                };

                                const handleStepNext = async () => {
                                    const stepChecks = validateStep(activeStep);
                                    Object.entries(stepChecks).forEach(([field, message]) => {
                                        setFieldTouched(field, true, false);
                                        setFieldError(field, message);
                                    });
                                    if (Object.values(stepChecks).some(Boolean)) {
                                        return;
                                    }
                                    setActiveStep((prev) => Math.min(prev + 1, 2));
                                };

                                const handleStepBack = () => {
                                    setActiveStep((prev) => Math.max(prev - 1, 0));
                                };

                                const renderStepContent = () => {
                                    switch (activeStep) {
                                        case 0:
                                            return (
                                                <>
                                                    <BodyRow as={Row}>
                                                        <Col xs={12} sm={12} md={6} lg={6}>
                                                            <FormSecondHeading>Postcode</FormSecondHeading>
                                                            <FormThirdHeading>
                                                                What is the postcode of the property?
                                                            </FormThirdHeading>
                                                        </Col>
                                                        <Col xs={12} sm={12} md={6} lg={6}>
                                                            <Form.Group>
                                                                <Form.Label>Property postcode</Form.Label>
                                                                <Form.Control
                                                                    type='text'
                                                                    name='postcode'
                                                                    placeholder='Enter your postcode'
                                                                    value={values.postcode}
                                                                    onChange={handleChange}
                                                                    isValid={touched.postcode && !errors.postcode}
                                                                    isInvalid={!!errors.postcode}
                                                                />
                                                                <Form.Control.Feedback type='invalid'>
                                                                    {errors.postcode}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>
                                                        </Col>
                                                    </BodyRow>
                                                    <BodyRow as={Row}>
                                                        <Col xs={12} sm={12} md={6} lg={6}>
                                                            <FormSecondHeading>Energy Usage</FormSecondHeading>
                                                            <FormThirdHeading>
                                                                Enter the household's approximate annual electricity use.
                                                            </FormThirdHeading>
                                                        </Col>
                                                        <Col xs={12} sm={12} md={6} lg={6}>
                                                            <Form.Group style={{ marginTop: '10px' }}>
                                                                <Form.Label>Annual electricity consumption (kWh)</Form.Label>
                                                                <Form.Control
                                                                    type='number'
                                                                    min='1'
                                                                    step='1'
                                                                    name='annualConsumptionKwh'
                                                                    value={values.annualConsumptionKwh}
                                                                    onChange={handleChange}
                                                                    isValid={touched.annualConsumptionKwh && !errors.annualConsumptionKwh}
                                                                    isInvalid={!!errors.annualConsumptionKwh}
                                                                />
                                                                <Form.Control.Feedback type='invalid'>
                                                                    {errors.annualConsumptionKwh}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>
                                                        </Col>
                                                    </BodyRow>
                                                </>
                                            );
                                        case 1:
                                            return (
                                                <>
                                                    <BodyRow as={Row}>
                                                        <Col xs={12} sm={12} md={6} lg={6}>
                                                            <FormSecondHeading>Size of installation</FormSecondHeading>
                                                            <FormThirdHeading>
                                                                Choose the panel count that best matches the roof space you plan to use.
                                                            </FormThirdHeading>
                                                        </Col>
                                                        <Col xs={12} sm={12} md={6} lg={6}>
                                                            <Form.Group style={{ marginTop: '10px' }}>
                                                                <Form.Label>System size</Form.Label>
                                                                <Form.Select
                                                                    name='size'
                                                                    value={values.size}
                                                                    onChange={handleChange}
                                                                    isValid={touched.size && !errors.size}
                                                                    isInvalid={!!errors.size}
                                                                >
                                                                    <option value="" disabled>Choose</option>
                                                                    {panelOptions.map(opt => (
                                                                        <option key={opt.value} value={opt.value}>
                                                                            {opt.label}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                                <Form.Control.Feedback type='invalid'>
                                                                    {errors.size}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>
                                                        </Col>
                                                    </BodyRow>
                                                    <BodyRow as={Row}>
                                                        <Col xs={12} sm={12} md={6} lg={6}>
                                                            <FormSecondHeading>Mounting location of panels</FormSecondHeading>
                                                            <FormThirdHeading>
                                                                Most homes use a roof-mounted setup, but we can estimate either option.
                                                            </FormThirdHeading>
                                                        </Col>
                                                        <Col xs={12} sm={12} md={6} lg={6}>
                                                            <Form.Group style={{ marginTop: '10px' }}>
                                                                <Form.Label>Mounting type</Form.Label>
                                                                <Form.Select
                                                                    name='fitting'
                                                                    value={values.fitting}
                                                                    onChange={handleChange}
                                                                    isValid={touched.fitting && !errors.fitting}
                                                                    isInvalid={!!errors.fitting}
                                                                >
                                                                    <option value="" disabled>Choose</option>
                                                                    <option value={'roof'}>Roof</option>
                                                                    <option value={'ground'}>Ground</option>
                                                                </Form.Select>
                                                                <Form.Control.Feedback type='invalid'>
                                                                    {errors.fitting}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>
                                                        </Col>
                                                    </BodyRow>
                                                    {values.fitting === 'roof' && (
                                                        <BodyRow as={Row}>
                                                            <Col xs={12} sm={12} md={6} lg={6}>
                                                                <FormSecondHeading>Roof Type</FormSecondHeading>
                                                                <FormThirdHeading>
                                                                    What type of roof is it?
                                                                </FormThirdHeading>
                                                            </Col>
                                                            <Col xs={12} sm={12} md={6} lg={6}>
                                                                <Form.Group style={{ marginTop: '10px' }}>
                                                                    <Form.Label>Roof type</Form.Label>
                                                                    <Form.Select
                                                                        name='type'
                                                                        value={values.type}
                                                                        onChange={handleChange}
                                                                        isValid={(touched.type && !errors.type) || values.fitting === 'ground'}
                                                                        isInvalid={!!errors.type}
                                                                    >
                                                                        <option value="" disabled>Choose</option>
                                                                        <option value={'tile'}>Tile</option>
                                                                        <option value={'slate'}>Slate</option>
                                                                        <option value={'trapezoidal'}>Trapezoidal</option>
                                                                        <option value={'corrugated'}>Corrugated</option>
                                                                        <option value={'standingSeam'}>Standing Seam</option>
                                                                        <option value={'kingspan'}>Kingspan</option>
                                                                        <option value={'vdvFlatRoof'}>VDV Flat Roof</option>
                                                                    </Form.Select>
                                                                    <Form.Control.Feedback type='invalid'>
                                                                        {errors.type}
                                                                    </Form.Control.Feedback>
                                                                </Form.Group>
                                                            </Col>
                                                        </BodyRow>
                                                    )}
                                                    {values.fitting === 'roof' && (
                                                        <BodyRow as={Row}>
                                                            <Col xs={12} sm={12} md={6} lg={6}>
                                                                <Row>
                                                                    <IconRow className="icon-row">
                                                                        <FormSecondHeading>Roof direction</FormSecondHeading>
                                                                        <div role="button" tabIndex={0} aria-label="Help with roof direction" onClick={() => openHelpModal('roof_direction_help', setRHModalIsOpen)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openHelpModal('roof_direction_help', setRHModalIsOpen); } }}>
                                                                            <QuestionSquareFill />
                                                                        </div>
                                                                    </IconRow>
                                                                </Row>
                                                                <FormThirdHeading>
                                                                    We need to know what direction your roof faces. The ideal is that it faces south, which is 180° on a compass.
                                                                </FormThirdHeading>
                                                            </Col>
                                                            <Col xs={12} sm={12} md={3} lg={3}>
                                                                <Form.Group style={{ marginTop: '10px' }}>
                                                                    <Form.Label>Direction roof faces</Form.Label>
                                                                    <Form.Select
                                                                        name='compassBearing'
                                                                        value={values.compassBearing}
                                                                        onChange={handleChange}
                                                                        isValid={(touched.compassBearing && !errors.compassBearing) || values.fitting === 'ground'}
                                                                        isInvalid={!!errors.compassBearing}
                                                                    >
                                                                        <option value="" disabled>Choose</option>
                                                                        {buildBearingOptions()}
                                                                    </Form.Select>
                                                                    <Form.Control.Feedback type='invalid'>
                                                                        {errors.compassBearing}
                                                                    </Form.Control.Feedback>
                                                                </Form.Group>
                                                            </Col>
                                                            <Col xs={12} sm={12} md={3} lg={3}>
                                                                <Form.Group style={{ marginTop: '10px' }}>
                                                                    <Form.Label>Compass Direction Name</Form.Label>
                                                                    <Form.Control
                                                                        name='direction'
                                                                        value={setDirectionString(values)}
                                                                        onChange={handleChange}
                                                                        readOnly
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                        </BodyRow>
                                                    )}
                                                    {values.fitting === 'roof' && (
                                                        <BodyRow as={Row}>
                                                            <Col xs={12} sm={12} md={6} lg={6}>
                                                                <Row>
                                                                    <IconRow className="icon-row">
                                                                        <FormSecondHeading>Roof slope</FormSecondHeading>
                                                                        <div role="button" tabIndex={0} aria-label="Help with roof slope" onClick={() => openHelpModal('roof_slope_help', setSHModalIsOpen)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openHelpModal('roof_slope_help', setSHModalIsOpen); } }}>
                                                                            <QuestionSquareFill />
                                                                        </div>
                                                                    </IconRow>
                                                                </Row>
                                                                <FormThirdHeading>
                                                                    This is the angle of your roof. An angle of 0° would be a completely flat roof, whereas 90° would be a situation where the panels were mounted on a vertical surface like a wall. Most roofs have an angle of 30°.
                                                                </FormThirdHeading>
                                                            </Col>
                                                            <Col xs={12} sm={12} md={6} lg={6}>
                                                                <Form.Group style={{ marginTop: '10px' }}>
                                                                    <Form.Label>Roof slope angle</Form.Label>
                                                                    <Form.Select
                                                                        name='roofSlope'
                                                                        value={values.roofSlope}
                                                                        onChange={handleChange}
                                                                        isValid={(touched.roofSlope && !errors.roofSlope) || values.fitting === 'ground'}
                                                                        isInvalid={!!errors.roofSlope}
                                                                    >
                                                                        <option value="" disabled>Choose</option>
                                                                        {buildSlopeOptions()}
                                                                    </Form.Select>
                                                                    <Form.Control.Feedback type='invalid'>
                                                                        {errors.roofSlope}
                                                                    </Form.Control.Feedback>
                                                                </Form.Group>
                                                            </Col>
                                                        </BodyRow>
                                                    )}
                                                    {values.fitting === 'roof' && (
                                                        <BodyRow as={Row}>
                                                            <Col xs={12} sm={12} md={6} lg={6}>
                                                                <Row>
                                                                    <IconRow className="icon-row">
                                                                        <FormSecondHeading>Roof shading</FormSecondHeading>
                                                                        <div role="button" tabIndex={0} aria-label="Help with roof shading" onClick={() => openHelpModal('roof_shading_help', setShadeModalIsOpen)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openHelpModal('roof_shading_help', setShadeModalIsOpen); } }}>
                                                                            <QuestionSquareFill />
                                                                        </div>
                                                                    </IconRow>
                                                                </Row>
                                                                <FormThirdHeading>
                                                                    This value refers to how much of your roof is in the shade. Have a look at the part of the roof where the panels will be fitted and try to judge how much shade is caused by objects such as trees or surrounding buildings.
                                                                </FormThirdHeading>
                                                            </Col>
                                                            <Col xs={12} sm={12} md={6} lg={6}>
                                                                <Form.Group style={{ marginTop: '10px' }}>
                                                                    <Form.Label>Shading level</Form.Label>
                                                                    <Form.Select
                                                                        name='shadeFactor'
                                                                        value={values.shadeFactor}
                                                                        onChange={handleChange}
                                                                        isValid={(touched.shadeFactor && !errors.shadeFactor) || values.fitting === 'ground'}
                                                                        isInvalid={!!errors.shadeFactor}
                                                                    >
                                                                        <option value="" disabled>Choose</option>
                                                                        <option value={0.96}>None 0%</option>
                                                                        <option value={0.875}>Low 1-25%</option>
                                                                        <option value={0.625}>Medium 25% - 50%</option>
                                                                        <option value={0.375}>High 50%-75%</option>
                                                                        <option value={0.125}>Very High 75%-100%</option>
                                                                    </Form.Select>
                                                                    <Form.Control.Feedback type='invalid'>
                                                                        {errors.shadeFactor}
                                                                    </Form.Control.Feedback>
                                                                </Form.Group>
                                                            </Col>
                                                        </BodyRow>
                                                    )}
                                                    <BodyRow as={Row}>
                                                    </BodyRow>
                                                </>
                                            );
                                        case 2:
                                            return (
                                                <>
                                                    <AddonsSection values={values} handleChange={handleChange} addons={addons || []} touched={touched} errors={errors} />
                                                    <BodyRow as={Row}>
                                                        <Col xs={12} sm={12} md={12} lg={12} className='text-center'>
                                                            <FormThirdHeading style={{ display: 'block' }}>
                                                                This estimate is based on typical assumptions for Northern Ireland, Great Britain and Ireland, so it is intended as a planning guide rather than a guaranteed performance figure.
                                                            </FormThirdHeading>
                                                            <button
                                                                type='button'
                                                                className='btn btn-outline-secondary btn-sm'
                                                                style={{ marginTop: '0.75rem' }}
                                                                onClick={() => setAssumptionsModalIsOpen(true)}
                                                            >
                                                                See how we calculate this
                                                            </button>
                                                        </Col>
                                                    </BodyRow>
                                                </>
                                            );
                                        default:
                                            return null;
                                    }
                                };

                                return (
                                    <CalculatorForm
                                        as={Form}
                                        onSubmit={formikSubmit}
                                        onChangeCapture={(event) => {
                                            startJourneyForm();
                                            noteJourneyField(event.target.name);
                                        }}
                                    >
                                        <BatteryInverterSync values={values} setFieldValue={setFieldValue} />
                                        <CalculatorStepper>
                                            {[{ label: 'Property' }, { label: 'Roof & system' }, { label: 'Extras' }].map((step, index) => {
                                                const isActive = activeStep === index;
                                                return (
                                                    <CalculatorStep key={step.label} $active={isActive}>
                                                        <CalculatorStepNumber $active={isActive}>
                                                            {index + 1}
                                                        </CalculatorStepNumber>
                                                        <span>{step.label}</span>
                                                    </CalculatorStep>
                                                );
                                            })}
                                        </CalculatorStepper>
                                        {renderStepContent()}
                                        <CalculatorActions>
                                            <div>
                                                <button type='button' className='btn btn-outline-secondary' onClick={handleStepBack} disabled={activeStep === 0}>
                                                    Back
                                                </button>
                                            </div>
                                            <div>
                                                {activeStep < 2 ? (
                                                    <button type='button' className='btn btn-success' onClick={handleStepNext}>
                                                        Next
                                                    </button>
                                                ) : (
                                                    <SubmitButton as='button' className='btn btn-success' type='submit' disabled={isLoading || !!dataError || pricingUnavailable || comparisonPricingUnavailable}>
                                                        {isCalculating ? (
                                                            <>
                                                                <LoadingSpinner size='sm' />
                                                                {' '}Calculate...
                                                            </>
                                                        ) : (
                                                            'Calculate'
                                                        )}
                                                    </SubmitButton>
                                                )}
                                            </div>
                                        </CalculatorActions>
                                    </CalculatorForm>
                                );
                            }}
                        </Formik>
                        <Footer />
                    </Col>
                </Row>
            </BodyContainer>
        </Container>
    );
}

export default Home;
